import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBooking, useCancelBooking, useExtendBooking } from '../api/queries'
import type { BookingDetails } from '../api/types'
import { cn } from '../lib/cn'
import { fmtDate, fmtDuration, fmtRange, minutesBetween } from '../lib/time'
import { people, receives } from '../lib/words'
import { useSession } from '../session'
import { DeclineDialog } from './DeclineDialog'
import { Dialog } from './Dialog'
import { ErrorNotice } from './ErrorNotice'
import { Badge, Button, Field, Input, Loading } from './ui'

/**
 * Карточка встречи со всеми действиями над ней.
 *
 * Права здесь — вторая ось: не «кто я в системе», а «кто я для этой встречи».
 * Организатор переносит и отменяет, участник может только сказать «не смогу прийти»,
 * администратор снимает чужую бронь, но обязан назвать причину.
 * Флаги can_edit / can_cancel / can_extend считает сервер — интерфейс их не пересчитывает.
 */
export function BookingDialog({
  bookingId,
  initial,
  onClose,
}: {
  bookingId: number
  /** Уже известные данные из списка: показываем сразу, не дожидаясь запроса. */
  initial?: BookingDetails
  onClose: () => void
}) {
  const { user } = useSession()
  const navigate = useNavigate()
  const { data, isLoading } = useBooking(bookingId)
  const booking = data ?? initial

  const extend = useExtendBooking()
  const [cancelling, setCancelling] = useState(false)
  const [declining, setDeclining] = useState(false)

  if (!booking) {
    return (
      <Dialog open onClose={onClose} title="Встреча">
        {isLoading ? <Loading /> : <p className="text-sm text-muted">Встречу не нашли.</p>}
      </Dialog>
    )
  }

  const isKiosk = booking.source === 'kiosk'
  const role = booking.my_role ?? 'outsider'
  const participants = booking.participants ?? []
  const going = participants.filter((participant) => participant.status === 'invited')

  return (
    <>
      <Dialog
        open={!cancelling && !declining}
        onClose={onClose}
        title={booking.title || 'Занято'}
        description={
          <span className="tnum">
            {booking.room?.name ?? booking.room_name ?? `Комната #${booking.room_id}`} ·{' '}
            {fmtDate(booking.starts_at)},{' '}
            {fmtRange(booking.starts_at, booking.ends_at)} ·{' '}
            {fmtDuration(minutesBetween(booking.starts_at, booking.ends_at))}
          </span>
        }
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Закрыть
            </Button>

            {role === 'participant' && (
              <Button variant="ghost" onClick={() => setDeclining(true)}>
                Не смогу прийти
              </Button>
            )}

            {booking.can_edit && (
              <Button variant="ghost" onClick={() => navigate(`/bookings/${booking.id}/edit`)}>
                Изменить
              </Button>
            )}

            {booking.can_cancel && (
              <Button variant="danger" onClick={() => setCancelling(true)}>
                Отменить
              </Button>
            )}

            {booking.can_extend && (
              <Button
                variant="primary"
                loading={extend.isPending}
                onClick={() => extend.mutate({ id: booking.id, minutes: 30 })}
              >
                + 30 минут
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <RoleBadge role={role} />
            {isKiosk && <Badge>Заняли с планшета</Badge>}
            {booking.recurrence && (
              <Badge tone="accent">
                {booking.recurrence.human_readable ?? 'Повторяющаяся серия'}
              </Badge>
            )}
          </div>

          {isKiosk && (
            <p className="hint">
              У этой брони нет организатора: комнату заняли прямо с планшета на двери.
              Освободить её можно там же — из веба она не снимается, отсюда не видно,
              вышли люди из комнаты или нет.
            </p>
          )}

          {booking.organizer && (
            <div>
              <p className="label">Организатор</p>
              <p className="text-sm">
                {booking.organizer.display_name}
                {booking.organizer.position && (
                  <span className="text-muted"> · {booking.organizer.position}</span>
                )}
              </p>
            </div>
          )}

          {participants.length > 0 && (
            <div>
              <p className="label">
                Участники: {going.length} из {participants.length}
              </p>
              <ul className="flex flex-wrap gap-2">
                {participants.map((participant) => (
                  <li
                    key={participant.user_id}
                    className={cn(
                      'rounded-lg border border-line px-2.5 py-1 text-sm',
                      participant.status === 'declined' && 'text-muted line-through',
                    )}
                    title={participant.email}
                  >
                    {participant.display_name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!booking.can_extend && role === 'organizer' && (
            <p className="hint">{extendRefusalHint(booking)}</p>
          )}

          {extend.error && <ErrorNotice error={extend.error} />}
        </div>
      </Dialog>

      {declining && (
        <DeclineDialog
          booking={booking}
          onClose={() => setDeclining(false)}
          // Отказавшемуся в карточке делать нечего: кнопка исчезнет, и останется
          // список участников, из которого его только что вычеркнули.
          onDeclined={onClose}
        />
      )}

      {cancelling && (
        <CancelBookingDialog
          booking={booking}
          requireReason={role !== 'organizer' && user.is_admin}
          onClose={() => setCancelling(false)}
          onCancelled={() => {
            setCancelling(false)
            onClose()
          }}
        />
      )}
    </>
  )
}

/** За сколько до начала встречи её уже можно продлить. То же, что extendLead в бэкенде. */
const EXTEND_LEAD_MS = 60 * 60 * 1000

/**
 * Почему у встречи нет кнопки «Продлить».
 *
 * Сам флаг can_extend по-прежнему считает сервер — здесь он не пересчитывается.
 * По времени встречи выбирается только объяснение: у одного и того же false
 * причины разные, а советы из них следуют противоположные. Раньше здесь всегда
 * стояло «следующий получас занят», и организатор встречи на послезавтра шёл
 * искать несуществующую чужую бронь вместо того, чтобы перенести свою.
 *
 * Границы совпадают с canExtendAt в internal/booking/dto.go: продлевается
 * идущая или начинающаяся в течение часа встреча.
 */
function extendRefusalHint(booking: BookingDetails): string {
  const now = Date.now()
  const startsAt = new Date(booking.starts_at).getTime()
  const endsAt = new Date(booking.ends_at).getTime()
  if (endsAt <= now) {
    return 'Встреча закончилась — продлевать уже нечего. Если нужно продолжить, забронируйте переговорную заново.'
  }
  if (startsAt - now >= EXTEND_LEAD_MS) {
    return 'Продлить можно встречу, которая уже идёт или начнётся в течение часа. Будущую перенесите — тогда участники получат письмо с новым временем.'
  }
  // Остаются две причины, и различить их здесь нечем: занятость следующего слота
  // и предел длительности из правил компании сервер в карточку не выносит.
  // Поэтому названы обе, а не наугад выбранная одна.
  return 'Продлить нельзя: либо следующий получас в этой комнате занят, либо встреча уже у предела длительности по правилам компании.'
}

function RoleBadge({ role }: { role: 'organizer' | 'participant' | 'outsider' }) {
  if (role === 'organizer') return <Badge tone="accent">Я организую</Badge>
  if (role === 'participant') return <Badge>Меня позвали</Badge>
  return <Badge>Чужая встреча</Badge>
}

/**
 * Экран 2.6 «Отмена». Диалог называет последствие: сколько человек получит письмо.
 * Администратору, снимающему чужую бронь, причина обязательна — она уходит в письмо.
 * Отмена чужой встречи без объяснения — самый быстрый способ поссорить людей с системой.
 *
 * Экспортируется, потому что отменяют встречу из двух мест: из карточки в списке
 * и со страницы встречи, на которую ведут ссылки из писем. Развилка «одна встреча
 * или серия» и обязательная причина обязаны быть в обоих одинаковыми.
 */
export function CancelBookingDialog({
  booking,
  requireReason,
  onClose,
  onCancelled,
}: {
  booking: BookingDetails
  requireReason: boolean
  onClose: () => void
  onCancelled: () => void
}) {
  const cancel = useCancelBooking()
  const [reason, setReason] = useState('')
  const [apply, setApply] = useState<'single' | 'series'>('single')

  const recipients = (booking.participants ?? []).filter(
    (participant) => participant.status === 'invited',
  ).length
  const isSeries = Boolean(booking.recurrence_id)
  const ready = !requireReason || reason.trim().length > 0

  return (
    <Dialog
      open
      onClose={onClose}
      title="Отменить встречу?"
      description={
        recipients > 0
          ? // Согласуется и глагол: «получат 1 человек» читается как опечатка,
            // а это первое, что человек видит в диалоге отмены чужой встречи.
            `Письмо об отмене ${receives(recipients)} ${people(recipients)}. Слот освободится сразу.`
          : 'Участников на встрече нет, письма никому не уйдут. Слот освободится сразу.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Оставить
          </Button>
          <Button
            variant="danger"
            disabled={!ready}
            loading={cancel.isPending}
            onClick={() =>
              cancel.mutate(
                {
                  id: booking.id,
                  reason: reason.trim() || undefined,
                  apply: isSeries ? apply : undefined,
                },
                { onSuccess: onCancelled },
              )
            }
          >
            Отменить встречу
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Экран 2.10: развилка «одна встреча или серия». Без явного выбора люди ломают серии. */}
        {isSeries && (
          <Field label="Что отменяем" group>
            <div className="flex flex-col gap-2">
              <Radio
                name="cancel-scope"
                checked={apply === 'single'}
                onChange={() => setApply('single')}
                label="Только эту встречу"
                hint={`${fmtDate(booking.starts_at)}, ${fmtRange(booking.starts_at, booking.ends_at)}`}
              />
              <Radio
                name="cancel-scope"
                checked={apply === 'series'}
                onChange={() => setApply('series')}
                label="Всю серию"
                hint={booking.recurrence?.human_readable ?? 'Все будущие встречи серии'}
              />
            </div>
          </Field>
        )}

        {requireReason && (
          <Field
            label="Причина отмены"
            hint="Уйдёт организатору и участникам в письме. Без неё отмена чужой встречи выглядит произволом."
          >
            <Input
              autoFocus
              maxLength={500}
              value={reason}
              placeholder="Например: комната нужна под собеседование"
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        )}

        {cancel.error && <ErrorNotice error={cancel.error} />}
      </div>
    </Dialog>
  )
}

function Radio({
  checked,
  onChange,
  label,
  hint,
  name,
}: {
  checked: boolean
  onChange: () => void
  label: string
  hint?: string
  /** Общее имя переключателей развилки: без него стрелки на клавиатуре не работают. */
  name: string
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition',
        checked ? 'border-accent bg-accent-soft/40' : 'border-line hover:bg-surface-2',
      )}
    >
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1 accent-current" />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="tnum block text-sm text-muted">{hint}</span>}
      </span>
    </label>
  )
}
